import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  where,
  type Firestore,
} from 'firebase/firestore';
import { getDb } from './firebase';
import type { OptionOrder } from './types';

export interface AttemptDoc {
  id: string;
  courseSlug: string;
  sectionSlug: string;
  picks: Record<string, OptionOrder>;
  crossed: Record<string, OptionOrder[]>;
  correctCount: number;
  totalQuestions: number;
  answeredCount: number;
  submittedAtMs: number;
  durationSeconds: number;
}

export interface CreateAttemptInput {
  courseSlug: string;
  sectionSlug: string;
  picks: Record<string, OptionOrder>;
  crossed: Record<string, OptionOrder[]>;
  correctCount: number;
  totalQuestions: number;
  answeredCount: number;
  durationSeconds: number;
}

function attemptsCollection(db: Firestore, uid: string) {
  return collection(db, 'users', uid, 'attempts');
}

function attemptDoc(db: Firestore, uid: string, attemptId: string) {
  return doc(db, 'users', uid, 'attempts', attemptId);
}

function snapshotToAttempt(id: string, data: Record<string, unknown>): AttemptDoc {
  const ts = data.submittedAt;
  const submittedAtMs = ts instanceof Timestamp ? ts.toMillis() : Date.now();
  return {
    id,
    courseSlug: (data.courseSlug as string) ?? '',
    sectionSlug: (data.sectionSlug as string) ?? '',
    picks: (data.picks ?? {}) as Record<string, OptionOrder>,
    crossed: (data.crossed ?? {}) as Record<string, OptionOrder[]>,
    correctCount: typeof data.correctCount === 'number' ? data.correctCount : 0,
    totalQuestions: typeof data.totalQuestions === 'number' ? data.totalQuestions : 0,
    answeredCount: typeof data.answeredCount === 'number' ? data.answeredCount : 0,
    submittedAtMs,
    durationSeconds: typeof data.durationSeconds === 'number' ? data.durationSeconds : 0,
  };
}

export async function createAttempt(uid: string, input: CreateAttemptInput): Promise<string> {
  const db = getDb();
  const ref = await addDoc(attemptsCollection(db, uid), {
    courseSlug: input.courseSlug,
    sectionSlug: input.sectionSlug,
    picks: input.picks,
    crossed: input.crossed,
    correctCount: input.correctCount,
    totalQuestions: input.totalQuestions,
    answeredCount: input.answeredCount,
    durationSeconds: input.durationSeconds,
    submittedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function getAttempt(uid: string, attemptId: string): Promise<AttemptDoc | null> {
  const db = getDb();
  const snap = await getDoc(attemptDoc(db, uid, attemptId));
  if (!snap.exists()) return null;
  return snapshotToAttempt(snap.id, snap.data());
}

/** All attempts for this user, sorted by submittedAt desc. */
export async function listAttempts(uid: string): Promise<AttemptDoc[]> {
  const db = getDb();
  const q = query(attemptsCollection(db, uid), orderBy('submittedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => snapshotToAttempt(d.id, d.data()));
}

/** All attempts for a specific (course, section) pair, newest first. */
export async function listAttemptsForSection(
  uid: string,
  courseSlug: string,
  sectionSlug: string,
): Promise<AttemptDoc[]> {
  const db = getDb();
  const q = query(
    attemptsCollection(db, uid),
    where('courseSlug', '==', courseSlug),
    where('sectionSlug', '==', sectionSlug),
    orderBy('submittedAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => snapshotToAttempt(d.id, d.data()));
}
